import { create } from "zustand";
import i18n from "../i18n.js";
import { createPersonaAPI, fetchUserPersonasAPI, deletePersonaAPI } from "../Services/userService.js";
import {
  storeSelectedPersonaId,
  getSelectedPersonaId,
  storeSuggestedPersonaDismissed,
  getSuggestedPersonaDismissed,
  storeShowFullPersona,
  getShowFullPersona,
} from "../Services/personaService.js";
import { generatePersonaSummaryHTML, updateURLWithPersonaSummary } from "../Utils/personaSummaryUtils.js";
import { decodeSearchText } from "../Utils/urlEncoder.js";
import { getCurrentURLQuery, extractPersonaFromURL, resolvePersonaSelection } from "../Utils/personaUrlUtils.js";

const usePersonaStore = create((set, get) => {
  const createSetter = (key) => (value) => set({ [key]: value });

  return {
    // Persona name from generated persona
    personaName: "",
    setPersonaName: createSetter("personaName"),

    // Persona image
    personaImage: null,
    setPersonaImage: createSetter("personaImage"),

    // List of all user personas
    userPersonas: [],
    setUserPersonas: createSetter("userPersonas"),

    // Currently selected persona
    selectedPersona: null,
    setSelectedPersona: (persona) => {
      const previousPersona = get().selectedPersona;
      set({ selectedPersona: persona });
      // Store in localStorage
      storeSelectedPersonaId(persona?._id || null);

      // Update showGeneratedPersonaAvatar based on selection
      const currentState = get();
      if (!persona && currentState.personaName && !currentState.suggestedPersonaDismissed) {
        // Show generated avatar when deselecting and we have generated data
        set({
          showGeneratedPersonaAvatar: true,
          showFullPersona: true, // Also restore full persona when deselecting
        });
      } else if (persona) {
        // Hide the generated avatar when any persona is selected
        // Also ensure showFullPersona is set to true to hide the CreatePersona component
        set({
          showGeneratedPersonaAvatar: false,
          showFullPersona: true
        });
      }

      // Trigger search when persona changes
      if (previousPersona?._id !== persona?._id) {
        // Import SearchStore dynamically to avoid circular dependency
        import("./SearchStore.jsx").then(async (SearchStore) => {
          const { setSearchText, getWantedGifts } =
            SearchStore.default.getState();

          // Create the persona summary HTML structure if persona is selected
          const newSearchText = generatePersonaSummaryHTML(persona);

          // Update the search text - this is the single source of truth
          setSearchText(newSearchText);

          // Store in localStorage for consistency
          if (newSearchText) {
            localStorage.setItem("searchText", newSearchText);
          } else {
            localStorage.removeItem("searchText");
          }

          // Only update URL if a persona is selected OR if we are already on the search page
          // This prevents navigating away from the home page when deselecting on mount
          const isSearchPage = window.location.pathname === "/search";
          const currentQuery = getCurrentURLQuery();
          if (persona || (isSearchPage && currentQuery)) {
            await updateURLWithPersonaSummary(newSearchText);
          }

          // If the new persona has a summary, trigger search
          if (persona && persona.summary) {
            // Get user info from cookies for the search
            const Cookies = await import("js-cookie");
            const cookie = Cookies.default.get();
            await getWantedGifts(cookie, false);
          }
        });
      }
    },

    // Generated persona data for unauthenticated users
    generatedPersonaData: null,
    setGeneratedPersonaData: createSetter("generatedPersonaData"),

    // Show create persona component for unauthenticated users
    showCreatePersonaForUnauthUser: false,
    setShowCreatePersonaForUnauthUser: createSetter(
      "showCreatePersonaForUnauthUser"
    ),

    // UI state for persona form
    showPersonaForm: false,
    setShowPersonaForm: createSetter("showPersonaForm"),

    // Form data for persona creation
    personaFormData: null,
    setPersonaFormData: createSetter("personaFormData"),

    // UI state for full persona display - restore from localStorage
    showFullPersona: getShowFullPersona(),
    setShowFullPersona: (value) => {
      set({ showFullPersona: value });
      storeShowFullPersona(value);
    },

    // UI state for persona shrinking animation
    isPersonaShrinking: false,
    setIsPersonaShrinking: createSetter("isPersonaShrinking"),

    // UI state for avatar hover
    isAvatarHovered: false,
    setIsAvatarHovered: createSetter("isAvatarHovered"),

    // Flag to show generated persona avatar (for authenticated users with no selection)
    // Initialize to false - will be set to true only when generating a new persona without selection
    showGeneratedPersonaAvatar: false,
    setShowGeneratedPersonaAvatar: createSetter("showGeneratedPersonaAvatar"),

    // Flag to track if suggested persona was dismissed
    suggestedPersonaDismissed: getSuggestedPersonaDismissed(),
    setSuggestedPersonaDismissed: (dismissed) => {
      set({ suggestedPersonaDismissed: dismissed });
      storeSuggestedPersonaDismissed(dismissed);
    },

    // Flag to track if persona is being created from plus button
    isCreatingFromPlusButton: false,
    setIsCreatingFromPlusButton: createSetter("isCreatingFromPlusButton"),

    // Flag to prevent Search.jsx from interfering during programmatic persona selection
    isProgrammaticSelection: false,
    setIsProgrammaticSelection: createSetter("isProgrammaticSelection"),

    // Flag to indicate a persona is being created (to prevent persona generation during creation)
    isCreatingPersona: false,
    setIsCreatingPersona: createSetter("isCreatingPersona"),

    // Helper method to sync persona to URL
    syncPersonaToURL: async (persona) => {
      const isSearchPage = typeof window !== 'undefined' && window.location.pathname === '/search';
      if (typeof window !== 'undefined' && persona && persona.summary && isSearchPage) {
        import("./SearchStore.jsx").then(async (SearchStore) => {
          const expectedSearchText = generatePersonaSummaryHTML(persona);
          const { setSearchText } = SearchStore.default.getState();
          setSearchText(expectedSearchText);
          await updateURLWithPersonaSummary(expectedSearchText);
        });
      }
    },

    // Fetch user personas from API
    fetchUserPersonas: async () => {
      try {
        const response = await fetchUserPersonasAPI();
        if (response.success) {
          set({ userPersonas: response.data });

          // Try to restore selected persona from localStorage
          const storedPersonaId = getSelectedPersonaId();
          const isSearchPage = window.location.pathname === "/search";

          if (storedPersonaId && response.data.length > 0 && isSearchPage) {
            const currentQuery = getCurrentURLQuery();
            let urlPersonaId = null;

            // Extract persona ID from URL if it exists
            if (currentQuery) {
              try {
                const decodedQuery = decodeSearchText(currentQuery);
                const urlData = extractPersonaFromURL(decodedQuery);
                urlPersonaId = urlData.personaId;
              } catch (error) {
                console.error("Error checking URL persona:", error);
              }
            }

            // Resolve which persona to select
            const resolution = resolvePersonaSelection(urlPersonaId, storedPersonaId, response.data);

            switch (resolution.action) {
              case 'restore':
                // Restore persona from localStorage
                set({
                  selectedPersona: resolution.persona,
                  showGeneratedPersonaAvatar: false,
                  showFullPersona: true // Ensure full persona is hidden when a persona is selected
                });
                // Sync URL if no query exists
                if (!currentQuery && resolution.persona.summary) {
                  get().syncPersonaToURL(resolution.persona);
                }
                break;

              case 'switch':
                // Switch to URL persona
                set({
                  selectedPersona: resolution.persona,
                  showGeneratedPersonaAvatar: false,
                  showFullPersona: true // Ensure full persona is hidden when a persona is selected
                });
                storeSelectedPersonaId(resolution.persona._id);
                break;

              case 'clear':
                // Clear selection completely
                set({
                  selectedPersona: null,
                  showGeneratedPersonaAvatar: false
                });
                storeSelectedPersonaId(null);
                break;

              case 'generate':
                // Clear selection to allow persona generation
                // Don't set showGeneratedPersonaAvatar to false - let the search flow handle it
                set({ selectedPersona: null });
                storeSelectedPersonaId(null);
                break;

              default:
                break;
            }

            return response.data;
          }

          // If no stored persona ID, ensure generated avatar is hidden if user has personas
          if (response.data.length > 0) {
            set({ showGeneratedPersonaAvatar: false });
          }

          return response.data;
        } else {
          console.error("Failed to fetch personas:", response.message);
          return [];
        }
      } catch (error) {
        console.error("Error fetching personas:", error);
        return [];
      }
    },

    // Create a new persona
    createPersona: async (personaData) => {
      try {
        const currentState = get();

        // Set flag to prevent persona generation during creation
        set({ isCreatingPersona: true });

        // Only send search text if:
        // 1. Not creating from plus button, OR
        // 2. Creating from plus button but no persona is selected
        const shouldSendSearchText = !currentState.isCreatingFromPlusButton || !currentState.selectedPersona;
        const usersearch = shouldSendSearchText ? (localStorage.getItem("prompt") || "") : "";

        const response = await createPersonaAPI(personaData, usersearch, i18n.language);
        if (response.success) {
          // Set the persona image in the store
          if (response.data.image) {
            set({ personaImage: response.data.image });
          }

          // Get the newly created persona from the API response
          const newPersona = response.data;

          if (newPersona && newPersona._id) {
            // Add the new persona to the userPersonas array
            const currentPersonas = get().userPersonas;
            set({ userPersonas: [...currentPersonas, newPersona] });

            // Set flag to prevent Search.jsx from interfering during selection
            set({ isProgrammaticSelection: true });

            // Clear generated persona state since we're creating a real persona
            set({
              showGeneratedPersonaAvatar: false,
              generatedPersonaData: null,
              personaName: "",
              showFullPersona: true
            });

            // Select the new persona (handles URL, search text, and search trigger)
            get().setSelectedPersona(newPersona);

            // Clear the flags after URL update completes
            setTimeout(() => {
              set({
                isProgrammaticSelection: false,
                isCreatingPersona: false
              });
            }, 500);
          } else {
            // Clear the flag if persona creation failed
            set({ isCreatingPersona: false });
          }

          // Reset the flag
          set({ isCreatingFromPlusButton: false });

          return { success: true, data: response.data };
        } else {
          console.error("Failed to create persona:", response.message);
          // Clear the flag on error
          set({ isCreatingPersona: false });
          return { success: false, message: response.message };
        }
      } catch (error) {
        console.error("Error creating persona:", error);
        // Clear the flag on error
        set({ isCreatingPersona: false });
        return { success: false, error };
      }
    },

    // Handle "Later" click on CreatePersona component
    handleLaterClick: () => {
      set({ isPersonaShrinking: true, suggestedPersonaDismissed: true });
      storeSuggestedPersonaDismissed(true);
      // After a short delay, hide the full persona and show only the circular avatar
      setTimeout(() => {
        set({ showFullPersona: false });
        storeShowFullPersona(false);
      }, 300);
    },

    // Handle avatar click to restore full persona and deselect any selected persona
    handleAvatarClick: () => {
      const currentState = get();

      // Deselect any selected persona when clicking the generated avatar
      if (currentState.selectedPersona) {
        set({ selectedPersona: null });
        storeSelectedPersonaId(null);
      }

      // Restore the full persona component if it was hidden
      if (!currentState.showFullPersona) {
        set({
          showFullPersona: true,
          isPersonaShrinking: false,
          showGeneratedPersonaAvatar: true // Ensure avatar stays visible
        });
        storeShowFullPersona(true);
      }
    },

    // Show persona form with optional form data
    openPersonaForm: (formData = null, fromPlusButton = false) => {
      const currentState = get();
      // If no formData provided but we have generated persona data, use it
      // BUT only if no persona is currently selected (when creating from plus button with selected persona, form should be empty)
      let dataToUse = formData;

      if (!dataToUse && currentState.generatedPersonaData && !currentState.selectedPersona) {
        const apiData = currentState.generatedPersonaData;

        // Parse hobbies from comma-separated string to array
        let hobbies = [];
        if (apiData.hobbies && apiData.hobbies.trim()) {
          hobbies = apiData.hobbies.split(',').map(h => h.trim());
        }

        // Parse personality from comma-separated string to array
        let personality = [];
        if (apiData.personality && apiData.personality.trim()) {
          personality = apiData.personality.split(',').map(p => p.trim());
        }

        dataToUse = {
          name: apiData.persona || "",
          age: apiData.age || "",
          gender: apiData.gender || "",
          hobbies: hobbies,
          personality: personality,
        };
      }

      set({
        personaFormData: dataToUse,
        showPersonaForm: true,
        isCreatingFromPlusButton: fromPlusButton,
      });
    },

    // Close persona form and refresh personas
    closePersonaForm: async () => {
      set({ showPersonaForm: false, isCreatingFromPlusButton: false });
      // Refresh the personas after closing the form
      await get().fetchUserPersonas();
    },

    // Update persona with full data from API response
    updatePersona: (updatedPersonaData) => {
      const currentState = get();

      // Update the persona in the userPersonas array
      const updatedPersonas = currentState.userPersonas.map(p =>
        p._id === updatedPersonaData._id
          ? { ...p, ...updatedPersonaData }
          : p
      );

      set({ userPersonas: updatedPersonas });

      // If this is the selected persona, update it too
      if (currentState.selectedPersona && currentState.selectedPersona._id === updatedPersonaData._id) {
        set({
          selectedPersona: {
            ...currentState.selectedPersona,
            ...updatedPersonaData
          }
        });
      }
    },

    // Delete a persona
    deletePersona: async (personaId) => {
      try {
        const response = await deletePersonaAPI(personaId);

        if (response.success) {
          const currentState = get();

          // Remove the persona from the userPersonas array
          const updatedPersonas = currentState.userPersonas.filter(
            p => p._id !== personaId
          );
          set({ userPersonas: updatedPersonas });

          // If the deleted persona was selected, deselect it
          if (currentState.selectedPersona && currentState.selectedPersona._id === personaId) {
            set({ selectedPersona: null });
            storeSelectedPersonaId(null);

            // Trigger search to refresh results
            import("./SearchStore.jsx").then(async (SearchStore) => {
              const { getWantedGifts } = SearchStore.default.getState();
              const Cookies = await import("js-cookie");
              const cookie = Cookies.default.get();
              await getWantedGifts(cookie, false);
            });
          }

          return { success: true };
        } else {
          console.error("Failed to delete persona:", response.message);
          return { success: false, message: response.message };
        }
      } catch (error) {
        console.error("Error deleting persona:", error);
        return { success: false, error };
      }
    },

    // Reset persona state (useful for logout)
    resetPersonaState: () => {
      set({
        personaName: "",
        personaImage: null,
        userPersonas: [],
        selectedPersona: null,
        generatedPersonaData: null,
        showCreatePersonaForUnauthUser: false,
        showPersonaForm: false,
        personaFormData: null,
        showFullPersona: true,
        isPersonaShrinking: false,
        isAvatarHovered: false,
        showGeneratedPersonaAvatar: false,
        suggestedPersonaDismissed: false,
        isCreatingPersona: false,
        isProgrammaticSelection: false,
      });
    },
  };
});

export default usePersonaStore;
